import { useQuery } from '@tanstack/react-query';

import { checkRepo } from '@/react/portainer/gitops/queries/useCheckRepo';
import { isPortainerError } from '@/portainer/error';

interface Creds {
  username?: string;
  password?: string;
  gitCredentialId?: number;
}

interface Params {
  url: string;
  creds?: Creds;
  force?: boolean;
  tlsSkipVerify?: boolean;
  createdFromCustomTemplateId?: number;
  stackId?: number;
  enabled?: boolean;
  onSettled?(isValid?: boolean): void;
  // run after onSettled, useful for clearing local flags like force
  onAfterSettle?(): void;
}

export function useGitRepoValidity({
  url,
  creds,
  force,
  tlsSkipVerify,
  createdFromCustomTemplateId,
  stackId,
  enabled,
  onSettled,
  onAfterSettle,
}: Params) {
  const options = {
    creds,
    force,
    tlsSkipVerify,
    createdFromCustomTemplateId,
    stackId,
  };

  const query = useQuery(
    ['git_repo_valid', url, options],
    () =>
      checkRepo(
        url,
        options as unknown as {
          creds?: Creds;
          tlsSkipVerify?: boolean;
          createdFromCustomTemplateId?: number;
          stackId?: number;
        }
      ),
    {
      enabled: !!url && !!enabled,
      retry: false,
      onSettled(isValid) {
        if (onSettled) {
          onSettled(isValid);
        }
        if (onAfterSettle) {
          onAfterSettle();
        }
      },
    }
  );

  const errorMessage = isPortainerError(query.error)
    ? query.error.message
    : undefined;

  const isChecking = query.isInitialLoading || query.isFetching;

  return {
    isValid: !!query.data && !query.isError,
    isChecking,
    hasError: query.isError,
    errorMessage,
    query,
  } as const;
}
